// Native agendy — BYZNYSOVÁ DATA (deklarace; žádná výkonná logika ani typy).
//
// Účel: JEDINÉ místo, které vyjmenovává, KTERÉ native (phoenix) moduly/agendy existují a jak se
// mapují legacy `ObjectCategory` → native agenda. Přidání nové native agendy = úprava jen tady.
// Rozdělení dle účelu: TYPY v nativeAgendaTypes · DATA zde · LOGIKA (agregace/instalace) v registry.
// Zdrojem jsou VÝHRADNĚ modulové barrely (veřejné kontrakty slices), nikdy internals.
//
// DŮLEŽITÉ: nepočítáme nic při module-init. Mezi tímto souborem (importuje modulové barrely),
// SlicePage (importují MenuNavigationShellPage) a MenuNavigationShellPage (importuje registry)
// vzniká cyklus. Kdybychom seznam sestavili do top-level konstanty, zachytila by seznamy agend ve
// chvíli, kdy jsou kvůli cyklu ještě neinicializované (`undefined`). Proto vše vracíme až za běhu
// z hoistovaných funkcí — v tu chvíli jsou všechny moduly načtené a live-bindings platné.

import {
  stationListAgenda,
  stationDetailAgenda,
  StationListPicker,
  stationChainListAgenda,
  productsStationsAgendas,
  productsChainsAgendas,
  venuaPlannerAgenda,
  venuaPlannerAgendas,
  placeholderListAgenda,
  subcategoryListAgenda,
  venuaPlannerPlaceholderAgendas,
  venuaPlannerPlaceholderSubcategoryAgendas,
  columbusAgenda,
  columbusAgendas,
} from '@/modules/products';
import {
  inventoryAirlineAgendas,
  inventoryVehicleAgendas,
  airlineListAgenda,
  vehicleListAgenda,
} from '@/modules/inventory';
import { businessCaseListAgenda, reservationsAgendas } from '@/modules/reservations';
import { rolePermissionsAgenda, securityAgendas } from '@/modules/security';
import { jobsAgendas, jobsOverviewAgenda } from '@/modules/jobs';
import { webFilterListAgenda, wwwWebFiltersAgendas } from '@/modules/www';

import type { NativeAgendaModule, NativeObjectCategoryAgenda } from './nativeAgendaTypes';

/**
 * Native moduly dostupné z menu (mimo dev-only design showcase, který nemá agendy).
 * Sestaveno až za běhu (viz pozn. o cyklu nahoře) — volat z efektů/handlerů, ne při module-init.
 */
export function getNativeAgendaModules(): readonly NativeAgendaModule[] {
  return [
    { path: '/products', rootAgenda: stationListAgenda, agendas: productsStationsAgendas },
    {
      path: '/products-chains',
      rootAgenda: stationChainListAgenda,
      agendas: productsChainsAgendas,
    },
    { path: '/venua-planner', rootAgenda: venuaPlannerAgenda, agendas: venuaPlannerAgendas },
    {
      path: '/placeholders',
      rootAgenda: placeholderListAgenda,
      agendas: venuaPlannerPlaceholderAgendas,
    },
    {
      path: '/placeholder-subcategories',
      rootAgenda: subcategoryListAgenda,
      agendas: venuaPlannerPlaceholderSubcategoryAgendas,
    },
    { path: '/columbus-planner', rootAgenda: columbusAgenda, agendas: columbusAgendas },
    {
      path: '/inventory-vehicles',
      rootAgenda: vehicleListAgenda,
      agendas: inventoryVehicleAgendas,
    },
    {
      path: '/inventory-airlines',
      rootAgenda: airlineListAgenda,
      agendas: inventoryAirlineAgendas,
    },
    { path: '/reservations', rootAgenda: businessCaseListAgenda, agendas: reservationsAgendas },
    { path: '/security', rootAgenda: rolePermissionsAgenda, agendas: securityAgendas },
    { path: '/web-filters', rootAgenda: webFilterListAgenda, agendas: wwwWebFiltersAgendas },
    // Operace na pozadí — bez položky v menu (otevírá se jen OpsIndicatorem v horní liště);
    // registrace tady zajišťuje floating okna + restore po reloadu z URL.
    { path: '/jobs', rootAgenda: jobsOverviewAgenda, agendas: jobsAgendas },
  ];
}

/**
 * ObjectCategory, které už mají native (phoenix) agendu. Sestaveno až za běhu (viz pozn. o cyklu
 * nahoře). Přidání položky sem = jediné místo, kterým se daná ObjectCategory přepne z legacy na
 * native ve všech navigacích (⋯/proklik v selektorech, sendMagicFormEdit, legacy `form/open`).
 */
export function getNativeObjectCategoryAgendas(): readonly NativeObjectCategoryAgenda[] {
  return [
    {
      objectCategory: 'Station',
      detail: stationDetailAgenda,
      toDetailParams: (id) => ({ stationId: Number(id) }),
      list: stationListAgenda,
      picker: StationListPicker,
    },
  ];
}
